import React, { useState } from "react";
import { Box, TextField, Button, Typography, Alert, Paper, Stack } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUser } from "../store/signupSlice";
import "./Auth.css";

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [msg, setMsg] = useState("");
  const [success, setSuccess] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetOtp, setResetOtp] = useState("");
  const [resetStep, setResetStep] = useState(1); // 1: email, 2: otp+newpass
  const [resetMsg, setResetMsg] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetNewPass, setResetNewPass] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleLogin = async e => {
    e.preventDefault();
    setMsg("");
    setSuccess(false);
    const res = await fetch("https://razchat-backend-1.onrender.com/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    const data = await res.json();
    if (res.ok && data.token) {
      localStorage.setItem("token", data.token);
      // Fetch user info after login to get username
      try {
        const tokenData = JSON.parse(atob(data.token.split('.')[1]));
        const userId = tokenData.userId;
        if (userId) {
          const userRes = await fetch(`https://razchat-backend-1.onrender.com/api/users/${userId}`, {
            headers: { Authorization: `Bearer ${data.token}` },
          });
          const user = await userRes.json();
          if (user && user.username && user.email) {
            dispatch(setUser({ username: user.username, email: user.email }));
          } else {
            dispatch(setUser({ email: form.email }));
          }
        } else {
          dispatch(setUser({ email: form.email }));
        }
      } catch {
        dispatch(setUser({ email: form.email }));
      }
      setSuccess(true);
      setMsg("Login successful!");
      setTimeout(() => navigate("/dashboard"), 500);
    } else {
      setMsg(data.error || "Login failed");
    }
  };

  const handleForgot = async e => {
    e.preventDefault();
    setResetMsg("");
    setResetSuccess(false);
    if (resetStep === 1) {
      // Send OTP
      const res = await fetch("https://razchat-backend-1.onrender.com/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail }),
      });
      const data = await res.json();
      if (res.ok) {
        setResetMsg("OTP sent to your email.");
        setResetStep(2);
      } else {
        setResetMsg(data.error || "Failed to send OTP");
      }
    } else if (resetStep === 2) {
      // Verify OTP and set new password
      const res = await fetch("https://razchat-backend-1.onrender.com/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail, otp: resetOtp, newPassword: resetNewPass }),
      });
      const data = await res.json();
      if (res.ok) {
        setResetMsg("Password reset successful! Please login.");
        setResetSuccess(true);
        setTimeout(() => {
          setShowForgot(false);
          setResetStep(1);
          setResetEmail("");
          setResetOtp("");
          setResetNewPass("");
          setResetMsg("");
        }, 1200);
      } else {
        setResetMsg(data.error || "Failed to reset password");
      }
    }
  };

  return (
    <div className="auth-bg">
      <Paper elevation={3} className="auth-paper">
        <Typography variant="h4" className="auth-title" align="center" mb={2}>
          Login
        </Typography>
        {!showForgot ? (
          <>
            <Box component="form" onSubmit={handleLogin}>
              <Stack spacing={2}>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  fullWidth
                  className="auth-input"
                />
                <TextField
                  name="password"
                  label="Password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  fullWidth
                  className="auth-input"
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  className="auth-btn"
                >
                  Login
                </Button>
              </Stack>
            </Box>
            {msg && (
              <Alert severity={success ? "success" : "error"} sx={{ mt: 2 }}>
                {msg}
              </Alert>
            )}
            <Typography mt={2} align="center">
              <span
                className="auth-link"
                style={{ cursor: "pointer" }}
                onClick={() => setShowForgot(true)}
              >
                Forgot password?
              </span>
            </Typography>
            <Typography mt={2} align="center">
              Don't have an account? <Link to="/signup" className="auth-link">Sign Up</Link>
            </Typography>
          </>
        ) : (
          <Box component="form" onSubmit={handleForgot}>
            <Stack spacing={2}>
              {resetStep === 1 ? (
                <>
                  <TextField
                    name="resetEmail"
                    label="Email"
                    type="email"
                    value={resetEmail}
                    onChange={e => setResetEmail(e.target.value)}
                    required
                    fullWidth
                    className="auth-input"
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    className="auth-btn"
                  >
                    Send OTP
                  </Button>
                </>
              ) : (
                <>
                  <TextField
                    name="resetOtp"
                    label="Enter OTP"
                    value={resetOtp}
                    onChange={e => setResetOtp(e.target.value)}
                    required
                    fullWidth
                    className="auth-input"
                  />
                  <TextField
                    name="resetNewPass"
                    label="New Password"
                    type="password"
                    value={resetNewPass}
                    onChange={e => setResetNewPass(e.target.value)}
                    required
                    fullWidth
                    className="auth-input"
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    className="auth-btn"
                  >
                    Reset Password
                  </Button>
                </>
              )}
            </Stack>
            {resetMsg && (
              <Alert severity={resetSuccess ? "success" : "error"} sx={{ mt: 2 }}>
                {resetMsg}
              </Alert>
            )}
            <Typography mt={2} align="center">
              <span
                className="auth-link"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setShowForgot(false);
                  setResetStep(1);
                  setResetEmail("");
                  setResetOtp("");
                  setResetNewPass("");
                  setResetMsg("");
                }}
              >
                Back to Login
              </span>
            </Typography>
          </Box>
        )}
      </Paper>
    </div>
  );
}
